/**
 * The media store (FEAT-038 wave 1) — where captured bytes live from now on.
 *
 * The disease this replaces caused three unopenable-app weeks in a row:
 * `captureDrafts` and `sendOutbox` both stored a moment's entire video as an
 * ArrayBuffer INSIDE the same IndexedDB record as its labels, so any read of
 * a record for any reason — counting drafts, refreshing a pill, resuming an
 * upload — deserialised whole videos into memory until iOS killed the app.
 * Three rounds of fixing individual readers each missed the next one.
 *
 * Here the split is structural, not disciplinary:
 *
 * - `media-meta`  — one SMALL record per item: dimensions, duration, edit
 *   fields, a ~20 KB thumbnail. There is no bytes field on the type
 *   (`bytes?: never`), so no metadata read can ever return a video.
 * - `media-chunks` — the bytes, as ~2 MiB rows keyed `[mediaId, seq]`.
 *   `MEDIA_CHUNK_BYTES === CHUNK_BYTES` on purpose: a stored chunk IS an
 *   upload PATCH body, so uploading any size of video needs ~one chunk of
 *   memory, never the file.
 *
 * Torn-write defence: chunks are written FIRST and the meta row LAST — the
 * meta row is the commit record. A kill mid-write leaves orphan chunks that
 * `sweepOrphanChunks()` collects (keys-only cursor; a value cursor over the
 * chunk store is the disease again). A meta row whose chunks don't check out
 * structurally reads as damaged (`assembleBlob` → null), never as a silent
 * bad upload.
 *
 * The `queue`, `sessions` and `kv` stores are created here (empty) so later
 * waves never need a version bump — a versionchange while another connection
 * is open is one of Safari IDB's classic ways to hang an app.
 *
 * Failure policy: this store FAILS LOUD (rejects). The old session store's
 * resolve-null-and-carry-on fallback made "not durable" look like "fine";
 * a queue that silently isn't durable is worse than one that refuses.
 */
import type { DrawingStroke, MomentOverlays } from '../../types';
import { CHUNK_BYTES } from '../uploadPolicy';

export const MEDIA_DB_NAME = 'momento-media';
export const MEDIA_DB_VERSION = 1;
export const META_STORE = 'media-meta';
export const CHUNK_STORE = 'media-chunks';
export const QUEUE_STORE = 'queue';
export const SESSION_STORE = 'sessions';
export const KV_STORE = 'kv';

/** One stored chunk is one upload PATCH body — the identity the whole
 * constant-memory upload path rests on. */
export const MEDIA_CHUNK_BYTES = CHUNK_BYTES;

/**
 * What this phone may hold (wave 3 — the 15-to-20-offline-videos mandate).
 *
 * The ITEM cap is the one that binds first, and by a mile: fifteen clips plus a
 * day of photos is past twenty-four long before any byte cap is near. Twenty-
 * four was a captureDrafts number from when a draft was a small thing.
 */
export const MAX_ITEMS = 64;

/** The byte budget when `storage.estimate()` is unavailable — the old flat cap,
 * kept as the conservative floor rather than as the rule. */
export const MAX_TOTAL_BYTES = 500 * 1024 * 1024;

/** Never more than this, whatever the origin is granted. Past 4 GiB the phone's
 * own storage warnings are a better guard than ours. */
const BUDGET_CEILING_BYTES = 4 * 1024 * 1024 * 1024;

export interface MediaMeta {
  id: string;
  userId: number;
  mediaType: 'photo' | 'video';
  /** The blob's MIME type, for reassembly. */
  mimeType: string;
  /** Total media bytes — with chunkCount, the structural commit record. */
  size: number;
  chunkCount: number;
  width: number;
  height: number;
  duration?: number;
  facing?: 'user' | 'environment';
  hd?: boolean;
  /** Draft-edit fields, moved verbatim from CaptureDraft (wave 2 swaps the
   * surfaces over; the semantics are theirs, not this store's). */
  kind: 'single' | 'batch-shot';
  batchId?: string;
  batchIndex?: number;
  textBars: { text: string; y: number }[];
  drawingStrokes: DrawingStroke[];
  overlays?: MomentOverlays | null;
  /** ~20 KB poster so tiles and queues render without touching chunks. */
  thumbnail?: { bytes: ArrayBuffer; type: string };
  createdAt: number;
  updatedAt: number;
  /** Reconcile stamps (BUG-065/075 machinery — `services/media/drafts.ts`). */
  sentKeys?: string[];
  sentAt?: number;
  /**
   * Wave 2: draft-ness is a FIELD here rather than a row in another database.
   * True = an unsent capture the drafts sheet offers back. False/absent = media
   * the queue owns (a send's payload, adopted from the retired outbox) and the
   * drafts sheet must not show. The two used to be separate stores, which is
   * how one capture could be counted, swept and deleted by two owners at once.
   */
  draft?: boolean;
  /** The disease, banned at the type level. */
  bytes?: never;
}

/** What a writer needs up front; size/chunkCount/updatedAt are computed. */
export type NewMediaMeta = Omit<
  MediaMeta,
  'size' | 'chunkCount' | 'updatedAt' | 'bytes'
>;

interface ChunkRow {
  mediaId: string;
  seq: number;
  bytes: ArrayBuffer;
}

/** One IDB request as a promise. Shared with the queue modules, which drive
 * the small-record stores through `withStore` below. */
export function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Streams bytes into the chunk store as they are produced (a recording's
 * muxer output, a blob's slices). Chunks land as the accumulator fills;
 * `finish()` flushes the tail and writes the meta row — the commit.
 */
export interface MediaWriter {
  write(bytes: Uint8Array): Promise<void>;
  finish(
    late?: Partial<Pick<MediaMeta, 'width' | 'height' | 'duration' | 'thumbnail' | 'overlays'>>
  ): Promise<MediaMeta>;
  /** Deletes everything written so far. Safe to call after a failed write. */
  abort(): Promise<void>;
  readonly bytesWritten: number;
}

export interface WriteOpts {
  /**
   * Skip the budget. For a write that is not new work: the evacuation moves
   * bytes a record ALREADY holds, and a batch promotion re-writes a capture
   * that is already counted — charging for both copies would refuse a phone
   * that is exactly as full as it was a moment ago.
   */
  bypassBudget?: boolean;
}

type ChangeCallback = () => void;

class MediaStoreService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private closed = false;
  private changeCallbacks = new Set<ChangeCallback>();
  /** Resolved once per foreground — the answer only changes when the phone's
   * own storage situation does. */
  private budgetPromise: Promise<number> | null = null;
  private watchingForeground = false;

  init(): Promise<void> {
    if (this.db || this.closed) return Promise.resolve();
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.open().catch((err) => {
      // A failed open must not poison every later call — allow a retry.
      this.initPromise = null;
      throw err;
    });
    return this.initPromise;
  }

  private open(attempt = 0): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('media store: IndexedDB open timed out'));
      }, 5000);

      const request = indexedDB.open(MEDIA_DB_NAME, MEDIA_DB_VERSION);

      request.onupgradeneeded = () => {
        // Store creation ONLY — data work in a versionchange transaction is
        // how the drafts v1→v2 migration got trapped (captureDrafts.ts).
        const db = request.result;
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(CHUNK_STORE)) {
          db.createObjectStore(CHUNK_STORE, { keyPath: ['mediaId', 'seq'] });
        }
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(SESSION_STORE)) {
          db.createObjectStore(SESSION_STORE, { keyPath: 'mediaId' });
        }
        if (!db.objectStoreNames.contains(KV_STORE)) {
          db.createObjectStore(KV_STORE, { keyPath: 'key' });
        }
      };

      request.onsuccess = () => {
        clearTimeout(timeout);
        if (this.closed) {
          request.result.close();
          resolve();
          return;
        }
        this.db = request.result;
        // A future version bump (there should never need to be one — all five
        // stores exist from v1) must not hang behind this connection.
        this.db.onversionchange = () => {
          this.db?.close();
          this.db = null;
          this.initPromise = null;
        };
        resolve();
      };

      request.onerror = () => {
        clearTimeout(timeout);
        // Safari's known first-open flake: one retry before failing loud.
        if (attempt === 0) {
          this.open(1).then(resolve, reject);
        } else {
          reject(request.error ?? new Error('media store: open failed'));
        }
      };
      request.onblocked = () => {
        clearTimeout(timeout);
        reject(new Error('media store: blocked by another connection'));
      };
    });
  }

  private async tx<T>(
    stores: string | string[],
    mode: IDBTransactionMode,
    run: (tx: IDBTransaction) => Promise<T> | T
  ): Promise<T> {
    await this.init();
    if (!this.db) throw new Error('media store: closed');
    const tx = this.db.transaction(stores, mode);
    return run(tx);
  }

  private req<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ---- writing ----

  /**
   * How many bytes this origin may hold: half its QUOTA, capped at 4 GiB.
   *
   * Quota, not free space — our own media counts against `usage`, so a rule
   * written against what is left would tighten as the app filled and start
   * refusing captures on a phone with room to spare. Half, because Momento is
   * a guest on a device with photos and messages of its own on it.
   */
  private byteBudget(): Promise<number> {
    if (!this.budgetPromise) {
      this.watchForeground();
      this.budgetPromise = (async () => {
        try {
          const quota = (await navigator.storage?.estimate?.())?.quota;
          if (!quota) return MAX_TOTAL_BYTES;
          // Not floored at MAX_TOTAL_BYTES on purpose: on a phone whose quota
          // really is small, a clean refusal beats a QuotaExceededError in the
          // middle of writing a clip.
          return Math.min(BUDGET_CEILING_BYTES, Math.floor(quota / 2));
        } catch {
          return MAX_TOTAL_BYTES;
        }
      })();
    }
    return this.budgetPromise;
  }

  /** A phone that was full an hour ago may not be now. */
  private watchForeground(): void {
    if (this.watchingForeground || typeof document === 'undefined') return;
    this.watchingForeground = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.budgetPromise = null;
    });
  }

  /**
   * Refuse a new item that will not fit, and tell the caller how much room is
   * left. A writer needs that number because its size is not known up front —
   * a recording's own `write()` enforces it as the bytes arrive.
   */
  private async assertRoom(userId: number, incomingBytes?: number): Promise<number> {
    const mine = await this.listMeta(userId);
    if (mine.length >= MAX_ITEMS) {
      throw new Error('media store: item budget reached');
    }
    const room =
      (await this.byteBudget()) - mine.reduce((sum, m) => sum + m.size, 0);
    if (incomingBytes !== undefined && incomingBytes > room) {
      throw new Error('media store: byte budget reached');
    }
    return room;
  }

  /**
   * Ask the browser to keep this data through a storage squeeze. Momento's
   * unsent moments are the one thing on this device that exists nowhere else,
   * so they are exactly what eviction must not take. Cheap, idempotent, and
   * the answer is the browser's to give — a refusal changes nothing else.
   */
  async requestPersistence(): Promise<boolean> {
    try {
      const storage = navigator.storage;
      if (!storage?.persisted) return false;
      if (await storage.persisted()) return true;
      return storage.persist ? await storage.persist() : false;
    } catch {
      return false;
    }
  }

  createWriter(meta: NewMediaMeta, opts?: WriteOpts): MediaWriter {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const store = this;
    const parts: Uint8Array[] = [];
    let buffered = 0;
    let seq = 0;
    let written = 0;
    let done = false;
    // One flush at a time: writes queue behind the previous put, which is
    // the backpressure that keeps a fast producer at ~one chunk of memory.
    let flushing: Promise<void> = Promise.resolve();
    // A recording's size is unknown when the writer opens, so the item check
    // happens here and the BYTE budget is enforced against the running total
    // inside `write()` — otherwise a long clip is a hole straight through the
    // budget, which is the one path that can actually fill a phone.
    let room = Number.POSITIVE_INFINITY;
    const roomChecked = opts?.bypassBudget
      ? Promise.resolve()
      : store.assertRoom(meta.userId).then((left) => {
          room = left;
        });

    const putChunk = (bytes: ArrayBuffer): Promise<void> => {
      const row: ChunkRow = { mediaId: meta.id, seq: seq++, bytes };
      written += bytes.byteLength;
      return store.tx(CHUNK_STORE, 'readwrite', (tx) =>
        store.req(tx.objectStore(CHUNK_STORE).put(row)).then(() => undefined)
      );
    };

    /** Assemble exactly MEDIA_CHUNK_BYTES from the buffer head. */
    const takeChunk = (): ArrayBuffer => {
      const out = new Uint8Array(MEDIA_CHUNK_BYTES);
      let offset = 0;
      while (offset < MEDIA_CHUNK_BYTES) {
        const head = parts[0];
        const need = MEDIA_CHUNK_BYTES - offset;
        if (head.byteLength <= need) {
          out.set(head, offset);
          offset += head.byteLength;
          parts.shift();
        } else {
          out.set(head.subarray(0, need), offset);
          parts[0] = head.subarray(need);
          offset += need;
        }
      }
      buffered -= MEDIA_CHUNK_BYTES;
      return out.buffer;
    };

    return {
      get bytesWritten() {
        return written + buffered;
      },

      async write(bytes: Uint8Array): Promise<void> {
        if (done) throw new Error('media store: writer already finished');
        await roomChecked;
        if (written + buffered + bytes.byteLength > room) {
          throw new Error('media store: byte budget reached');
        }
        // Copy: the caller may reuse/transfer its buffer (muxers do).
        parts.push(new Uint8Array(bytes));
        buffered += bytes.byteLength;
        while (buffered >= MEDIA_CHUNK_BYTES) {
          const chunk = takeChunk();
          flushing = flushing.then(() => putChunk(chunk));
        }
        await flushing;
      },

      async finish(late = {}): Promise<MediaMeta> {
        if (done) throw new Error('media store: writer already finished');
        done = true;
        await roomChecked;
        if (buffered > 0) {
          const tail = new Uint8Array(buffered);
          let offset = 0;
          for (const part of parts) {
            tail.set(part, offset);
            offset += part.byteLength;
          }
          parts.length = 0;
          buffered = 0;
          flushing = flushing.then(() => putChunk(tail.buffer));
        }
        await flushing;

        // Meta LAST — this write is the commit. Everything before it is
        // invisible garbage the orphan sweep owns.
        const full: MediaMeta = {
          ...meta,
          ...late,
          size: written,
          chunkCount: seq,
          updatedAt: Date.now(),
        };
        await store.tx(META_STORE, 'readwrite', (tx) =>
          store.req(tx.objectStore(META_STORE).put(full))
        );
        store.fireChange();
        return full;
      },

      async abort(): Promise<void> {
        done = true;
        parts.length = 0;
        buffered = 0;
        await roomChecked.catch(() => {});
        await flushing.catch(() => {});
        await store.deleteChunks(meta.id);
      },
    };
  }

  /**
   * Store a whole blob, constant memory: sequential `slice().arrayBuffer()`
   * reads — a Blob slice is lazy, so only ~one chunk is ever resident above
   * whatever backs the blob itself.
   */
  async saveBlob(meta: NewMediaMeta, blob: Blob, opts?: WriteOpts): Promise<MediaMeta> {
    if (!opts?.bypassBudget) await this.assertRoom(meta.userId, blob.size);
    // The size was known up front and has just been charged for, so the writer
    // must not ask again — a second `assertRoom` is a second walk of every
    // metadata row, posters and all, for an answer we already have.
    const writer = this.createWriter(
      { ...meta, mimeType: meta.mimeType || blob.type },
      { ...opts, bypassBudget: true }
    );
    try {
      for (let offset = 0; offset < blob.size; offset += MEDIA_CHUNK_BYTES) {
        const piece = await blob
          .slice(offset, Math.min(offset + MEDIA_CHUNK_BYTES, blob.size))
          .arrayBuffer();
        await writer.write(new Uint8Array(piece));
      }
      return await writer.finish();
    } catch (err) {
      await writer.abort().catch(() => {});
      throw err;
    }
  }

  // ---- reading (metadata never returns bytes — the type forbids it) ----

  async meta(id: string): Promise<MediaMeta | null> {
    return this.tx(META_STORE, 'readonly', async (tx) => {
      const row = await this.req(tx.objectStore(META_STORE).get(id));
      return (row as MediaMeta | undefined) ?? null;
    });
  }

  /** All of a user's items — meta store only, safe by construction. */
  async listMeta(userId: number): Promise<MediaMeta[]> {
    return this.tx(META_STORE, 'readonly', async (tx) => {
      const rows = (await this.req(tx.objectStore(META_STORE).getAll())) as MediaMeta[];
      return rows
        .filter((m) => m.userId === userId)
        .sort((a, b) => a.createdAt - b.createdAt);
    });
  }

  /** Every item's id, keys only — the whole store without a single record. */
  async allMetaIds(): Promise<string[]> {
    return this.tx(META_STORE, 'readonly', async (tx) => {
      return (await this.req(tx.objectStore(META_STORE).getAllKeys())) as string[];
    });
  }

  async totalBytes(userId: number): Promise<number> {
    return (await this.listMeta(userId)).reduce((sum, m) => sum + m.size, 0);
  }

  async readChunk(id: string, seq: number): Promise<ArrayBuffer | null> {
    return this.tx(CHUNK_STORE, 'readonly', async (tx) => {
      const row = (await this.req(tx.objectStore(CHUNK_STORE).get([id, seq]))) as
        | ChunkRow
        | undefined;
      return row?.bytes ?? null;
    });
  }

  /**
   * The bytes starting at `offset`, up to the end of the chunk that contains
   * it — at most MEDIA_CHUNK_BYTES, and never spanning two chunks.
   *
   * This is the upload loop's only read. A resume adopts whatever offset the
   * server states, which need not sit on one of our boundaries (an older build
   * uploaded with a different chunk size), so the first body after such an
   * adoption is short and every one after it is aligned again. One chunk is
   * resident at a time whatever the file's size.
   */
  async readAt(id: string, offset: number): Promise<Uint8Array | null> {
    const seq = Math.floor(offset / MEDIA_CHUNK_BYTES);
    const bytes = await this.readChunk(id, seq);
    if (!bytes) return null;
    const within = offset - seq * MEDIA_CHUNK_BYTES;
    if (within >= bytes.byteLength) return null;
    return new Uint8Array(bytes, within);
  }

  /**
   * Direct access to the small-record stores — the send queue's ledger, its
   * upload sessions, and the crash-safe scratchpad. The CHUNK store is
   * deliberately unreachable this way: bytes leave this module as one Blob
   * (`assembleBlob`) or one chunk (`readAt`), never as a store to walk.
   */
  async withStore<T>(
    name: typeof QUEUE_STORE | typeof SESSION_STORE | typeof KV_STORE,
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => Promise<T> | T
  ): Promise<T> {
    return this.tx(name, mode, (tx) => run(tx.objectStore(name)));
  }

  /**
   * The whole media as one Blob — ONE item, on a user's tap (preview, editor
   * resume), never in a loop. Each chunk is wrapped in its own sub-Blob so
   * the buffers can be released as they're read; the final Blob references
   * the sub-Blobs rather than copying them.
   *
   * Returns null when the chunks don't match the meta's commit record —
   * a torn write surfaces as damaged, never as a silent bad upload.
   */
  async assembleBlob(id: string): Promise<Blob | null> {
    const meta = await this.meta(id);
    if (!meta) return null;
    const pieces: Blob[] = [];
    let total = 0;
    for (let seq = 0; seq < meta.chunkCount; seq++) {
      const bytes = await this.readChunk(id, seq);
      if (!bytes) return null; // torn: a committed meta must have every chunk
      total += bytes.byteLength;
      pieces.push(new Blob([bytes]));
    }
    if (total !== meta.size) return null;
    return new Blob(pieces, { type: meta.mimeType });
  }

  // ---- mutating ----

  /** One-transaction read-modify-write of ONE meta row. */
  async updateMeta(id: string, mutate: (m: MediaMeta) => MediaMeta): Promise<boolean> {
    const changed = await this.tx(META_STORE, 'readwrite', (tx) => {
      return new Promise<boolean>((resolve, reject) => {
        const store = tx.objectStore(META_STORE);
        const request = store.get(id);
        request.onsuccess = () => {
          const row = request.result as MediaMeta | undefined;
          if (!row) {
            resolve(false);
            return;
          }
          const next = { ...mutate(row), updatedAt: Date.now() };
          const put = store.put(next);
          put.onsuccess = () => resolve(true);
          put.onerror = () => reject(put.error);
        };
        request.onerror = () => reject(request.error);
      });
    });
    if (changed) this.fireChange();
    return changed;
  }

  private deleteChunks(id: string): Promise<void> {
    return this.tx(CHUNK_STORE, 'readwrite', (tx) =>
      this.req(
        tx.objectStore(CHUNK_STORE).delete(
          // Every seq for this media: [id, 0] .. [id, +∞)
          IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER])
        )
      ).then(() => undefined)
    );
  }

  async remove(ids: string[], _reason: 'delivered' | 'discarded' | 'expired'): Promise<void> {
    for (const id of ids) {
      await this.tx(META_STORE, 'readwrite', (tx) =>
        this.req(tx.objectStore(META_STORE).delete(id))
      );
      await this.deleteChunks(id);
    }
    if (ids.length > 0) this.fireChange();
  }

  /**
   * Delete chunk runs that have no meta row — the leavings of a write that
   * never committed. Keys-only cursor: a value cursor over the chunk store
   * would deserialise the bytes it exists to protect.
   * Returns how many medias' orphans were cleared.
   */
  async sweepOrphanChunks(): Promise<number> {
    const metaIds = await this.tx(META_STORE, 'readonly', async (tx) => {
      const keys = (await this.req(tx.objectStore(META_STORE).getAllKeys())) as string[];
      return new Set(keys);
    });
    const orphanIds = await this.tx(CHUNK_STORE, 'readonly', (tx) => {
      return new Promise<Set<string>>((resolve, reject) => {
        const found = new Set<string>();
        const request = tx.objectStore(CHUNK_STORE).openKeyCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) {
            resolve(found);
            return;
          }
          const [mediaId] = cursor.primaryKey as [string, number];
          if (!metaIds.has(mediaId)) found.add(mediaId);
          cursor.continue();
        };
        request.onerror = () => reject(request.error);
      });
    });
    for (const id of orphanIds) await this.deleteChunks(id);
    return orphanIds.size;
  }

  // ---- small durable facts (kv) ----

  /**
   * A crash-safe scratchpad in the same database as the media, so a note about
   * a write and the write itself can never disagree about which one survived.
   */
  async kvGet<T>(key: string): Promise<T | null> {
    return this.tx(KV_STORE, 'readonly', async (tx) => {
      const row = await this.req(tx.objectStore(KV_STORE).get(key));
      return (row as T | undefined) ?? null;
    });
  }

  async kvSet(key: string, value: Record<string, unknown>): Promise<void> {
    await this.tx(KV_STORE, 'readwrite', (tx) =>
      this.req(tx.objectStore(KV_STORE).put({ key, ...value }))
    );
  }

  async kvDelete(key: string): Promise<void> {
    await this.tx(KV_STORE, 'readwrite', (tx) =>
      this.req(tx.objectStore(KV_STORE).delete(key))
    );
  }

  // ---- change signal ----

  /** Fires immediately on subscribe (surfaces render current state first). */
  onChange(cb: ChangeCallback): () => void {
    this.changeCallbacks.add(cb);
    cb();
    return () => this.changeCallbacks.delete(cb);
  }

  private fireChange(): void {
    this.changeCallbacks.forEach((cb) => cb());
  }

  // ---- lifecycle (tests) ----

  close(): void {
    this.closed = true;
    this.initPromise = null;
    this.db?.close();
    this.db = null;
  }

  /** Tests only: reopenable after close(), and with the budget re-asked. */
  _reopen(): void {
    this.closed = false;
    this.budgetPromise = null;
  }
}

export const mediaStore = new MediaStoreService();
